import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { isActiveAdmin, type V1Admin } from '../../../lib/v1';
import { Icon } from '../../../components/icons';
import { adminLabel } from './helpers';

/* ─── AssigneeSelect — Durchsuchbare Auswahl der zuständigen Person ───────── */

/**
 * Aufgaben haben genau EINE zuständige Person (Feedback 11.10.2026): eine
 * Auswahl ersetzt die vorherige, erneuter Klick auf die gewählte Person
 * entfernt sie. Das Datenformat bleibt ein Array (`bearbeiter: [id]`), damit
 * v1 und v2 unverändert lesen. `multiple` schaltet die alte Mehrfachauswahl ein.
 */
export function AssigneeSelect({
  dark, admins, value, onChange, compact, multiple = false,
}: {
  dark: boolean;
  admins: V1Admin[];
  value: number[];
  onChange: (ids: number[]) => void;
  compact?: boolean;
  multiple?: boolean;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const toggle = (id: number) => {
    if (!multiple) {
      onChange(value.includes(id) ? [] : [id]);
      setQuery('');
      setOpen(false);
      return;
    }
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  };
  const remove = (id: number) => onChange(value.filter(x => x !== id));

  const byId = React.useMemo(() => new Map(admins.map(a => [a.id, a])), [admins]);

  // Typeahead-Filter über Name + E-Mail; wählbar sind nur aktive Mitarbeiter
  // (Chips bereits zugewiesener inaktiver bleiben über `byId` benannt).
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = admins.filter(isActiveAdmin);
    if (!q) return active;
    return active.filter(a =>
      adminLabel(a).toLowerCase().includes(q) || (a.email ?? '').toLowerCase().includes(q),
    );
  }, [admins, query]);

  // Klick außerhalb schließt das Dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const fontLabel = compact ? 10.5 : 12;

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
      <span style={{ fontSize: fontLabel, fontWeight: 600, color: tc.muted }}>
        {t('pflege.detail.dashboard.task_assignee_label')}
      </span>

      {/* Gewählte Bearbeiter als entfernbare Chips */}
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.map(id => {
            const a = byId.get(id);
            return (
              <span
                key={id}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 11.5, fontWeight: 600, borderRadius: 999,
                  padding: '3px 6px 3px 10px',
                  border: `1px solid ${BRAND}`, background: BRAND_LIGHT, color: BRAND,
                }}
              >
                {a ? adminLabel(a) : `#${id}`}
                <button
                  type="button"
                  aria-label={t('pflege.detail.dashboard.task_assignee_remove', { name: a ? adminLabel(a) : `#${id}` })}
                  onClick={(e) => { e.stopPropagation(); remove(id); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 16, height: 16, borderRadius: 999, border: 'none',
                    background: 'transparent', color: BRAND, cursor: 'pointer', padding: 0,
                    fontSize: 13, lineHeight: 1,
                  }}
                >×</button>
              </span>
            );
          })}
        </div>
      )}

      {/* Suchfeld → typeahead-Filter der Admin-Liste */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 8, display: 'flex' }}><Icon.Search c={tc.placeholder} size={13} /></span>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="assignee-listbox"
          aria-label={t('pflege.detail.dashboard.task_assignee_search')}
          placeholder={t('pflege.detail.dashboard.task_assignee_search')}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={e => { e.stopPropagation(); setOpen(true); }}
          style={{
            fontSize: 12.5, border: `1px solid ${tc.border}`, borderRadius: 7,
            padding: '6px 10px 6px 28px', width: '100%', boxSizing: 'border-box',
            background: tc.inputBg, color: tc.text, outline: 'none', fontFamily: 'inherit',
          }}
        />
      </div>

      {open && (
        <div
          id="assignee-listbox"
          role="listbox"
          aria-label={t('pflege.detail.dashboard.task_assignee_label')}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30,
            maxHeight: 200, overflowY: 'auto', padding: 4,
            background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {admins.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>
              {t('pflege.detail.dashboard.task_assignee_none')}
            </div>
          )}
          {admins.length > 0 && filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: tc.muted }}>
              {t('common.search.empty')}
            </div>
          )}
          {filtered.map(a => {
            const selected = value.includes(a.id);
            return (
              <button
                key={a.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={(e) => { e.stopPropagation(); toggle(a.id); if (multiple) inputRef.current?.focus(); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  width: '100%', textAlign: 'left', border: 'none', borderRadius: 7,
                  padding: '7px 10px', fontSize: 12.5, cursor: 'pointer',
                  background: selected ? BRAND_LIGHT : 'transparent',
                  color: selected ? BRAND : tc.text, fontWeight: selected ? 600 : 400,
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminLabel(a)}</span>
                {selected && <Icon.Check c={BRAND} size={12} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
